/**
 * Environment Configuration Module
 * Gerencia variáveis de ambiente com validação e valores padrão
 */

/**
 * Schema de validação para variáveis de ambiente
 */
const ENV_SCHEMA = {
    // API Configuration
    STRAPI_API_URL: {
        type: 'string',
        // Usa variável injetada pelo Vite no build ou fallback para localhost
        default: import.meta.env.VITE_STRAPI_API_URL || 'http://localhost:1337',
        required: false,
        validate: (value) => {
            if (!value) return true;
            try {
                new URL(value);
                return true;
            } catch {
                return 'STRAPI_API_URL deve ser uma URL válida';
            }
        }
    },

    STRAPI_API_TOKEN: {
        type: 'string',
        default: '',
        required: false
    },

    MOTORDELEADS_API_URL: {
        type: 'string',
        default: import.meta.env.VITE_MOTORDELEADS_API_URL || 'https://api-gateway.mundozerokm.com.br/v1/webhook',
        required: false,
        validate: (value) => {
            if (!value) return true;
            try {
                new URL(value);
                return true;
            } catch {
                return 'MOTORDELEADS_API_URL deve ser uma URL válida';
            }
        }
    },

    API_TIMEOUT: {
        type: 'number',
        default: 10000,
        required: false,
        validate: (value) => value > 0 || 'API_TIMEOUT deve ser maior que 0'
    },

    // Environment
    NODE_ENV: {
        type: 'string',
        default: 'development',
        required: false,
        validate: (value) =>
            ['development', 'production', 'test'].includes(value) ||
            'NODE_ENV deve ser development, production ou test'
    },

    DEBUG: {
        type: 'boolean',
        default: false,
        required: false
    },

    // Cache Configuration
    CACHE_ENABLED: {
        type: 'boolean',
        default: true,
        required: false
    },

    CACHE_TTL: {
        type: 'number',
        default: 300000, // 5 minutos
        required: false,
        validate: (value) => value > 0 || 'CACHE_TTL deve ser maior que 0'
    },

    CACHE_MAX_SIZE: {
        type: 'number',
        default: 100,
        required: false,
        validate: (value) => value > 0 || 'CACHE_MAX_SIZE deve ser maior que 0'
    },

    // Widget Configuration
    WIDGET_CSS_PREFIX: {
        type: 'string',
        default: 'mzk-widget',
        required: false
    },

    LAZY_LOADING: {
        type: 'boolean',
        default: true,
        required: false
    },

    CAROUSEL_AUTOPLAY_DELAY: {
        type: 'number',
        default: 5000,
        required: false,
        validate: (value) => value >= 0 || 'CAROUSEL_AUTOPLAY_DELAY deve ser maior ou igual a 0'
    },

    // Analytics & Monitoring
    GA_TRACKING_ID: {
        type: 'string',
        default: '',
        required: false
    },

    ERROR_REPORTING: {
        type: 'boolean',
        default: true,
        required: false
    },

    ERROR_ENDPOINT: {
        type: 'string',
        default: '',
        required: false
    },

    // Performance
    PRELOAD_MODULES: {
        type: 'boolean',
        default: false,
        required: false
    },

    PRELOAD_MODULE_LIST: {
        type: 'string',
        default: '',
        required: false
    },

    MAX_IMAGE_SIZE: {
        type: 'number',
        default: 500,
        required: false,
        validate: (value) => value > 0 || 'MAX_IMAGE_SIZE deve ser maior que 0'
    }
};

/**
 * Classe para gerenciar variáveis de ambiente
 */
export class EnvConfig {
    constructor() {
        this.values = {};
        this.errors = [];
        this.loaded = false;
    }

    /**
     * Carrega variáveis de ambiente
     * @param {Object} envVars - Objeto com variáveis de ambiente
     * @returns {EnvConfig} - Instância para encadeamento
     */
    load(envVars = {}) {
        this.values = {};
        this.errors = [];

        // Processa cada variável do schema
        for (const [key, schema] of Object.entries(ENV_SCHEMA)) {
            const rawValue = envVars[key];
            const value = this._parseValue(rawValue, schema);

            // Valida a variável
            const error = this._validateValue(key, value, schema);
            if (error) {
                this.errors.push(error);
                // Usa valor padrão em caso de erro
                this.values[key] = schema.default;
            } else {
                this.values[key] = value;
            }
        }

        this.loaded = true;
        return this;
    }

    /**
     * Carrega de process.env (Node.js)
     * @returns {EnvConfig}
     */
    loadFromProcess() {
        if (typeof process !== 'undefined' && process.env) {
            return this.load(process.env);
        }
        return this.load({});
    }

    /**
     * Carrega de window (Browser)
     * @returns {EnvConfig}
     */
    loadFromWindow() {
        if (typeof window !== 'undefined' && window.MZK_ENV) {
            return this.load(window.MZK_ENV);
        }
        return this.load({});
    }

    /**
     * Carrega automaticamente (detecta ambiente)
     * @returns {EnvConfig}
     */
    loadAuto() {
        // Tenta Node.js primeiro
        if (typeof process !== 'undefined' && process.env) {
            return this.loadFromProcess();
        }

        // Depois tenta browser
        if (typeof window !== 'undefined' && window.MZK_ENV) {
            return this.loadFromWindow();
        }

        // Fallback para valores padrão
        return this.load({});
    }

    /**
     * Obtém valor de uma variável
     * @param {string} key - Nome da variável
     * @param {*} defaultValue - Valor padrão se não encontrado
     * @returns {*}
     */
    get(key, defaultValue = undefined) {
        if (!this.loaded) {
            this.loadAuto();
        }

        return this.values.hasOwnProperty(key)
            ? this.values[key]
            : (defaultValue !== undefined ? defaultValue : ENV_SCHEMA[key]?.default);
    }

    /**
     * Define valor de uma variável
     * @param {string} key - Nome da variável
     * @param {*} value - Valor
     */
    set(key, value) {
        if (!ENV_SCHEMA[key]) {
            throw new Error(`Variável de ambiente desconhecida: ${key}`);
        }

        const schema = ENV_SCHEMA[key];
        const parsedValue = this._parseValue(value, schema);
        const error = this._validateValue(key, parsedValue, schema);

        if (error) {
            throw new Error(error);
        }

        this.values[key] = parsedValue;
    }

    /**
     * Retorna todos os valores
     * @returns {Object}
     */
    getAll() {
        if (!this.loaded) {
            this.loadAuto();
        }
        return { ...this.values };
    }

    /**
     * Verifica se há erros
     * @returns {boolean}
     */
    hasErrors() {
        return this.errors.length > 0;
    }

    /**
     * Retorna erros de validação
     * @returns {Array<string>}
     */
    getErrors() {
        return [...this.errors];
    }

    /**
     * Verifica se está em modo desenvolvimento
     * @returns {boolean}
     */
    isDevelopment() {
        return this.get('NODE_ENV') === 'development';
    }

    /**
     * Verifica se está em modo produção
     * @returns {boolean}
     */
    isProduction() {
        return this.get('NODE_ENV') === 'production';
    }

    /**
     * Verifica se está em modo teste
     * @returns {boolean}
     */
    isTest() {
        return this.get('NODE_ENV') === 'test';
    }

    /**
     * Verifica se debug está ativo
     * @returns {boolean}
     */
    isDebug() {
        return this.get('DEBUG') === true;
    }

    /**
     * Parse de valor baseado no schema
     * @private
     */
    _parseValue(rawValue, schema) {
        // Se não tem valor, usa padrão
        if (rawValue === undefined || rawValue === null || rawValue === '') {
            return schema.default;
        }

        // Parse baseado no tipo
        switch (schema.type) {
            case 'boolean':
                if (typeof rawValue === 'boolean') return rawValue;
                return rawValue === 'true' || rawValue === '1';

            case 'number':
                if (typeof rawValue === 'number') return rawValue;
                const num = parseFloat(rawValue);
                return isNaN(num) ? schema.default : num;

            case 'string':
                return String(rawValue);

            default:
                return rawValue;
        }
    }

    /**
     * Validação de valor
     * @private
     */
    _validateValue(key, value, schema) {
        // Verifica obrigatoriedade
        if (schema.required && (value === undefined || value === null || value === '')) {
            return `Variável ${key} é obrigatória`;
        }

        // Validação customizada
        if (schema.validate) {
            const result = schema.validate(value);
            if (result !== true) {
                return `${key}: ${result}`;
            }
        }

        return null;
    }
}

// Instância singleton
const envConfig = new EnvConfig();

// Auto-load na inicialização
envConfig.loadAuto();

// Exporta instância e classe
export default envConfig;
export { ENV_SCHEMA };
